<template>
  <div class="space-y-6">
    <!-- Back Button -->
    <button
      @click="handleBack"
      class="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition"
    >
      <svg
        class="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15 19l-7-7 7-7"
        />
      </svg>
      <span class="text-xl font-medium">Back</span>
    </button>

    <!-- Wrestler Info Card -->
    <div class="bg-white rounded-xl shadow-md p-4 sm:p-6">
      <p
        class="text-sm sm:text-base font-semibold text-gray-500 uppercase tracking-wide mb-2"
      >
        A few questions for
      </p>
      <div class="flex">
        <!-- Avatar -->
        <img
          v-if="wrestler.avatar_thumbnail"
          :src="wrestler.avatar_thumbnail"
          class="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover mr-3 sm:mr-4 flex-shrink-0"
          alt=""
        />
        <span
          v-else
          class="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-200 mr-3 sm:mr-4 flex items-center justify-center flex-shrink-0"
        >
          <svg
            class="w-6 h-6 sm:w-8 sm:h-8 text-gray-400"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
              clip-rule="evenodd"
            />
          </svg>
        </span>

        <!-- Name -->
        <div class="flex-1 min-w-0 flex items-center">
          <span class="font-semibold text-gray-900 text-xl sm:text-2xl break-words">
            {{ wrestler.display_name || wrestler.full_name }}
          </span>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="bg-white rounded-xl shadow-md p-8">
      <div class="flex items-center justify-center">
        <svg
          class="animate-spin h-8 w-8 text-gray-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <span class="ml-3 text-gray-600 text-lg">Loading questions...</span>
      </div>
    </div>

    <!-- Questions Form -->
    <div v-else class="bg-white rounded-xl shadow-md p-4 sm:p-6 space-y-6">
      <!-- Question Components -->
      <div v-for="question in questions" :key="question.id" class="space-y-2">
        <!-- Short Text Question -->
        <kiosk-question-short-text
          v-if="question.type === 'RegShortTextQuestion'"
          :question="question"
          :existing-answer="getExistingAnswer(question)"
          @answer-changed="handleAnswerChanged"
        />

        <!-- Email Question -->
        <kiosk-question-email
          v-else-if="question.type === 'RegEmailQuestion'"
          :question="question"
          :existing-answer="getExistingAnswer(question)"
          @answer-changed="handleAnswerChanged"
        />

        <!-- Phone Question -->
        <kiosk-question-phone
          v-else-if="question.type === 'RegPhoneNumberQuestion'"
          :question="question"
          :existing-answer="getExistingAnswer(question)"
          @answer-changed="handleAnswerChanged"
        />

        <!-- Date Question -->
        <kiosk-question-date
          v-else-if="question.type === 'RegDateQuestion'"
          :question="question"
          :existing-answer="getExistingAnswer(question)"
          @answer-changed="handleAnswerChanged"
        />

        <!-- Yes/No Question -->
        <kiosk-question-yes-no
          v-else-if="question.type === 'RegYesNoQuestion'"
          :question="question"
          :existing-answer="getExistingAnswer(question)"
          @answer-changed="handleAnswerChanged"
        />

        <!-- Single Select Question -->
        <kiosk-question-single-select
          v-else-if="question.type === 'RegSingleSelectQuestion'"
          :question="question"
          :existing-answer="getExistingAnswer(question)"
          @answer-changed="handleAnswerChanged"
        />

        <!-- Multi Select Question -->
        <kiosk-question-multi-select
          v-else-if="question.type === 'RegMultiSelectQuestion'"
          :question="question"
          :existing-answer="getExistingAnswer(question)"
          @answer-changed="handleAnswerChanged"
        />

        <!-- Address Question -->
        <kiosk-question-address
          v-else-if="question.type === 'RegAddressQuestion'"
          :question="question"
          :existing-answer="getExistingAnswer(question)"
          @answer-changed="handleAnswerChanged"
        />

        <!-- Photo Question -->
        <kiosk-question-photo
          v-else-if="question.type === 'RegPhotoQuestion'"
          :question="question"
          :existing-answer="getExistingAnswer(question)"
          :endpoint="endpoint"
          :access-key="accessKey"
          :cloudfront-base-url="cloudfrontBaseUrl"
          @answer-changed="handleAnswerChanged"
        />

        <!-- Unsupported Question Type (fallback) -->
        <div v-else class="p-4 bg-gray-100 rounded-lg">
          <p class="text-gray-500">Unsupported question type: {{ question.type }}</p>
        </div>
      </div>

      <!-- Error Message -->
      <div
        v-if="errorMessage"
        class="p-4 bg-red-50 border border-red-200 rounded-lg"
      >
        <p class="text-red-600 font-medium">{{ errorMessage }}</p>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-4 pt-4">
        <!-- Skip Button (only if all questions are optional) -->
        <button
          v-if="allQuestionsOptional"
          @click="handleSkip"
          class="flex-1 py-4 px-6 text-xl font-semibold rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition"
        >
          Skip
        </button>

        <!-- Submit Button -->
        <button
          @click="handleSubmit"
          :disabled="saving"
          class="flex-1 py-4 px-6 text-xl font-semibold rounded-lg transition"
          :style="{
            backgroundColor: saving ? '#9CA3AF' : 'var(--team-primary)',
            color: 'var(--team-text)',
          }"
        >
          <span v-if="saving" class="flex items-center justify-center gap-2">
            <svg
              class="animate-spin h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Saving...
          </span>
          <span v-else>Continue</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

// Question components - will be created
import KioskQuestionShortText from '../../shared/components/questions/QuestionShortText.vue';
import KioskQuestionEmail from '../../shared/components/questions/QuestionEmail.vue';
import KioskQuestionPhone from '../../shared/components/questions/QuestionPhone.vue';
import KioskQuestionDate from '../../shared/components/questions/QuestionDate.vue';
import KioskQuestionYesNo from '../../shared/components/questions/QuestionYesNo.vue';
import KioskQuestionSingleSelect from '../../shared/components/questions/QuestionSingleSelect.vue';
import KioskQuestionMultiSelect from '../../shared/components/questions/QuestionMultiSelect.vue';
import KioskQuestionAddress from '../../shared/components/questions/QuestionAddress.vue';
import KioskQuestionPhoto from '../../shared/components/questions/QuestionPhoto.vue';

export default {
  name: 'KioskQuestionsForm',
  components: {
    KioskQuestionShortText,
    KioskQuestionEmail,
    KioskQuestionPhone,
    KioskQuestionDate,
    KioskQuestionYesNo,
    KioskQuestionSingleSelect,
    KioskQuestionMultiSelect,
    KioskQuestionAddress,
    KioskQuestionPhoto,
  },
  props: {
    questions: {
      type: Array,
      required: true,
    },
    checkIn: {
      type: Object,
      required: true,
    },
    wrestler: {
      type: Object,
      required: true,
    },
    kioskId: {
      type: Number,
      required: true,
    },
    // S3 props for photo uploads
    endpoint: {
      type: String,
      default: '',
    },
    accessKey: {
      type: String,
      default: '',
    },
    cloudfrontBaseUrl: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: true,
      saving: false,
      errorMessage: '',
      existingAnswers: [],
      // Map of question_id to answer data
      answers: {},
    };
  },
  computed: {
    allQuestionsOptional() {
      return this.questions.every((q) => !q.required);
    },
    hasRequiredUnanswered() {
      return this.questions.some((q) => {
        if (!q.required) return false;
        const answerData = this.answers[q.id];
        if (!answerData) return true;
        return !answerData.valid;
      });
    },
  },
  created() {
    this.loadExistingAnswers();
  },
  methods: {
    async loadExistingAnswers() {
      this.loading = true;
      try {
        // Use team API endpoint (kiosk uses team API key, not user token)
        const url = `/api/team/v1/registration_answers?profile_id=${this.wrestler.id}&profile_type=WrestlerProfile&check_in_id=${this.checkIn.id}`;
        const response = await axios.get(url);
        this.existingAnswers = response.data.registration_answers || [];
        this.initializeAnswers();
      } catch (error) {
        console.error('Failed to load existing answers:', error);
        // Continue with empty answers - not a blocking error
        this.existingAnswers = [];
        this.initializeAnswers();
      } finally {
        this.loading = false;
      }
    },
    initializeAnswers() {
      // Pre-populate answers map from existing answers
      this.questions.forEach((question) => {
        const existing = this.getExistingAnswer(question);
        if (existing) {
          this.answers[question.id] = {
            id: existing.id,
            answer: existing.answer,
            dirty: false,
            valid: true, // Existing answers are valid
          };
        } else {
          // Initialize empty answer
          this.answers[question.id] = {
            id: null,
            answer: this.getEmptyAnswerForType(question.type),
            dirty: false,
            valid: !question.required, // Empty is valid only if not required
          };
        }
      });
    },
    getEmptyAnswerForType(type) {
      switch (type) {
        case 'RegYesNoQuestion':
          return { answer: '', addtl_info: '' };
        case 'RegMultiSelectQuestion':
          return [];
        case 'RegAddressQuestion':
          return {
            line1: '', line2: '', city: '', state: '', zip: '',
          };
        case 'RegPhotoQuestion':
          return {
            attachment_id: null, s3_file_name: '', file_name: '', status: '',
          };
        default:
          return '';
      }
    },
    getExistingAnswer(question) {
      return this.existingAnswers.find(
        (a) => a.registration_question_id === question.id,
      );
    },
    handleAnswerChanged(question, answer, dirty, valid) {
      this.answers[question.id] = {
        id: this.answers[question.id]?.id || null,
        answer,
        dirty,
        valid,
      };
      // Clear error when user makes changes
      this.errorMessage = '';
    },
    handleBack() {
      // Emit back to go back to event selection
      // Note: This will discard any answers in progress
      this.$emit('back');
    },
    handleSkip() {
      this.$emit('skip');
    },
    async handleSubmit() {
      // Validate required questions
      if (this.hasRequiredUnanswered) {
        this.errorMessage = 'Please answer all required questions.';
        // Trigger validation display on question components
        this.$notificationManager?.emit('show-reg-errors');
        return;
      }

      this.saving = true;
      this.errorMessage = '';

      try {
        // Build answers payload - only include dirty answers
        const answersToSave = this.questions
          .filter((q) => {
            const answerData = this.answers[q.id];
            // Save if dirty OR if it's a new answer with content
            return answerData && (answerData.dirty || (!answerData.id && this.hasContent(answerData.answer)));
          })
          .map((q) => {
            const answerData = this.answers[q.id];
            return {
              id: answerData.id,
              registration_question_id: q.id,
              profile_id: this.wrestler.id,
              profile_type: 'WrestlerProfile',
              check_in_id: this.checkIn.id,
              answer: answerData.answer,
              respondant_id: this.kioskId,
              respondant_type: 'Kiosk',
              dirty: true,
              valid: answerData.valid,
            };
          });

        if (answersToSave.length > 0) {
          // Use team API endpoint (kiosk uses team API key, not user token)
          await axios.post('/api/team/v1/registration_answers/batch', {
            registration_answers: answersToSave,
          });
        }

        this.$emit('complete');
      } catch (error) {
        console.error('Failed to save answers:', error);
        this.errorMessage = 'Failed to save answers. Please try again.';
      } finally {
        this.saving = false;
      }
    },
    hasContent(answer) {
      if (answer === null || answer === undefined) return false;
      if (typeof answer === 'string') return answer.length > 0;
      if (Array.isArray(answer)) return answer.length > 0;
      if (typeof answer === 'object') {
        // For YesNo questions
        if ('answer' in answer) return answer.answer && answer.answer.length > 0;
        // For Address questions
        if ('line1' in answer) return answer.line1 && answer.line1.length > 0;
        // For Photo questions
        if ('s3_file_name' in answer) return answer.s3_file_name && answer.s3_file_name.length > 0;
      }
      return false;
    },
  },
};
</script>
